import { createContext, useContext, useRef, useCallback, useState, ReactNode } from 'react';

interface AudioContextValue {
  getAnalyser: () => AnalyserNode | null;
  isPlaying: boolean;
  playVintageJazz: () => void;
}

const AudioContextCtx = createContext<AudioContextValue | null>(null);

export const useAudioContext = () => {
  const ctx = useContext(AudioContextCtx);
  if (!ctx) {
    throw new Error('useAudioContext must be used within AudioProvider');
  }
  return ctx;
};

export const AudioProvider = ({ children }: { children: ReactNode }) => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 64;
      analyserRef.current.smoothingTimeConstant = 0.8;
      analyserRef.current.connect(audioContextRef.current.destination);
    }
    return audioContextRef.current;
  }, []);

  const getAnalyser = useCallback(() => {
    getAudioContext();
    return analyserRef.current;
  }, [getAudioContext]);

  const playVintageJazz = useCallback(() => {
    const audioContext = getAudioContext();
    const analyser = analyserRef.current!;
    
    setIsPlaying(true);

    // Multiple jazz phrases - randomly selected
    const jazzPhrases = [
      // Phrase 1: Classic Bb blues lick (Louis Armstrong style)
      [
        { freq: 233.08, delay: 0, duration: 0.6 },      // Bb3
        { freq: 277.18, delay: 0.5, duration: 0.4 },    // Db4
        { freq: 311.13, delay: 0.85, duration: 0.3 },   // Eb4
        { freq: 329.63, delay: 1.1, duration: 0.5 },    // E4
        { freq: 349.23, delay: 1.5, duration: 0.6 },    // F4
        { freq: 415.30, delay: 2.0, duration: 1.0 },    // Ab4
      ],
      // Phrase 2: Descending blues run
      [
        { freq: 466.16, delay: 0, duration: 0.5 },      // Bb4
        { freq: 415.30, delay: 0.4, duration: 0.4 },    // Ab4
        { freq: 349.23, delay: 0.75, duration: 0.35 },  // F4
        { freq: 329.63, delay: 1.05, duration: 0.4 },   // E4
        { freq: 311.13, delay: 1.4, duration: 0.5 },    // Eb4
        { freq: 233.08, delay: 1.85, duration: 1.2 },   // Bb3 (hold)
      ],
      // Phrase 3: Swing phrase with triplet feel
      [
        { freq: 293.66, delay: 0, duration: 0.3 },      // D4
        { freq: 349.23, delay: 0.25, duration: 0.25 },  // F4
        { freq: 392.00, delay: 0.45, duration: 0.3 },   // G4
        { freq: 466.16, delay: 0.7, duration: 0.6 },    // Bb4
        { freq: 392.00, delay: 1.25, duration: 0.3 },   // G4
        { freq: 349.23, delay: 1.5, duration: 0.8 },    // F4
        { freq: 293.66, delay: 2.2, duration: 0.9 },    // D4
      ],
      // Phrase 4: What a Wonderful World opening style
      [
        { freq: 261.63, delay: 0, duration: 0.7 },      // C4
        { freq: 329.63, delay: 0.6, duration: 0.5 },    // E4
        { freq: 392.00, delay: 1.0, duration: 0.6 },    // G4
        { freq: 523.25, delay: 1.5, duration: 0.9 },    // C5
        { freq: 493.88, delay: 2.3, duration: 0.7 },    // B4
        { freq: 440.00, delay: 2.9, duration: 1.0 },    // A4
      ],
      // Phrase 5: Scat-like rhythmic phrase
      [
        { freq: 349.23, delay: 0, duration: 0.2 },      // F4
        { freq: 349.23, delay: 0.18, duration: 0.15 },  // F4
        { freq: 392.00, delay: 0.35, duration: 0.25 },  // G4
        { freq: 440.00, delay: 0.55, duration: 0.4 },   // A4
        { freq: 392.00, delay: 0.9, duration: 0.2 },    // G4
        { freq: 349.23, delay: 1.08, duration: 0.5 },   // F4
        { freq: 293.66, delay: 1.5, duration: 0.8 },    // D4
        { freq: 261.63, delay: 2.2, duration: 1.0 },    // C4
      ],
      // Phrase 6: High energy bebop lick
      [
        { freq: 392.00, delay: 0, duration: 0.2 },      // G4
        { freq: 440.00, delay: 0.15, duration: 0.15 },  // A4
        { freq: 493.88, delay: 0.28, duration: 0.2 },   // B4
        { freq: 523.25, delay: 0.45, duration: 0.35 },  // C5
        { freq: 587.33, delay: 0.75, duration: 0.5 },   // D5
        { freq: 523.25, delay: 1.2, duration: 0.3 },    // C5
        { freq: 440.00, delay: 1.45, duration: 0.4 },   // A4
        { freq: 392.00, delay: 1.8, duration: 1.0 },    // G4
      ],
    ];

    // Randomly select a phrase
    const trumpetNotes = jazzPhrases[Math.floor(Math.random() * jazzPhrases.length)];

    // Different chord progressions to accompany
    const chordProgressions = [
      [130.81, 164.81, 196.00, 233.08], // Cm7
      [146.83, 174.61, 220.00, 261.63], // Dm7
      [164.81, 196.00, 246.94, 293.66], // Em7
      [174.61, 220.00, 261.63, 329.63], // Fmaj7
      [196.00, 246.94, 293.66, 369.99], // G7
    ];
    
    const pianoChord = chordProgressions[Math.floor(Math.random() * chordProgressions.length)];

    const now = audioContext.currentTime;

    // Create muted trumpet sound for each note
    trumpetNotes.forEach(({ freq, delay, duration }) => {
      const osc1 = audioContext.createOscillator();
      const osc2 = audioContext.createOscillator();
      const vibratoOsc = audioContext.createOscillator();
      const vibratoGain = audioContext.createGain();
      const mainGain = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();
      const distortion = audioContext.createWaveShaper();

      osc1.type = 'sawtooth';
      osc1.frequency.setValueAtTime(freq, now + delay);
      
      osc2.type = 'sawtooth';
      osc2.frequency.setValueAtTime(freq * 1.002, now + delay);

      vibratoOsc.type = 'sine';
      vibratoOsc.frequency.setValueAtTime(5 + Math.random() * 2, now + delay);
      vibratoGain.gain.setValueAtTime(3 + Math.random() * 3, now + delay);
      
      vibratoOsc.connect(vibratoGain);
      vibratoGain.connect(osc1.frequency);
      vibratoGain.connect(osc2.frequency);

      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(700 + Math.random() * 300, now + delay);
      filter.frequency.linearRampToValueAtTime(1000 + Math.random() * 400, now + delay + 0.1);
      filter.frequency.linearRampToValueAtTime(500 + Math.random() * 200, now + delay + duration);
      filter.Q.setValueAtTime(1.5 + Math.random(), now + delay);

      const curve = new Float32Array(256);
      for (let i = 0; i < 256; i++) {
        const x = (i / 128) - 1;
        curve[i] = Math.tanh(x * 1.5);
      }
      distortion.curve = curve;

      const volume = 0.1 + Math.random() * 0.04;
      mainGain.gain.setValueAtTime(0, now + delay);
      mainGain.gain.linearRampToValueAtTime(volume, now + delay + 0.06);
      mainGain.gain.setValueAtTime(volume * 0.85, now + delay + 0.12);
      mainGain.gain.linearRampToValueAtTime(volume * 0.7, now + delay + duration * 0.7);
      mainGain.gain.exponentialRampToValueAtTime(0.001, now + delay + duration);

      osc1.connect(filter);
      osc2.connect(filter);
      filter.connect(distortion);
      distortion.connect(mainGain);
      mainGain.connect(analyser);

      osc1.start(now + delay);
      osc2.start(now + delay);
      vibratoOsc.start(now + delay);
      osc1.stop(now + delay + duration + 0.1);
      osc2.stop(now + delay + duration + 0.1);
      vibratoOsc.stop(now + delay + duration + 0.1);
    });

    // Soft piano chord in background
    pianoChord.forEach((freq, i) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now);

      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(700, now);

      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.025, now + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 3.5);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(analyser);

      osc.start(now + i * 0.015);
      osc.stop(now + 3.5);
    });

    // Calculate total duration based on the phrase
    const maxDuration = Math.max(...trumpetNotes.map(n => n.delay + n.duration));
    const totalDuration = maxDuration + 0.8;

    // Vinyl crackle and noise effect
    const createVinylNoise = () => {
      const bufferSize = audioContext.sampleRate * totalDuration;
      const noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
      const output = noiseBuffer.getChannelData(0);

      // Create subtle continuous hiss
      for (let i = 0; i < bufferSize; i++) {
        output[i] = (Math.random() * 2 - 1) * 0.008;
      }

      // Add random pops and crackles
      const numPops = Math.floor(totalDuration * 3); // ~3 pops per second
      for (let p = 0; p < numPops; p++) {
        const popPosition = Math.floor(Math.random() * bufferSize);
        const popIntensity = 0.03 + Math.random() * 0.04;
        const popLength = Math.floor(20 + Math.random() * 60);
        
        for (let i = 0; i < popLength && popPosition + i < bufferSize; i++) {
          const decay = 1 - (i / popLength);
          output[popPosition + i] += (Math.random() * 2 - 1) * popIntensity * decay;
        }
      }

      // Add occasional scratches
      const numScratches = Math.floor(totalDuration * 0.5);
      for (let s = 0; s < numScratches; s++) {
        const scratchStart = Math.floor(Math.random() * bufferSize);
        const scratchLength = Math.floor(200 + Math.random() * 400);
        const scratchFreq = 0.1 + Math.random() * 0.2;
        
        for (let i = 0; i < scratchLength && scratchStart + i < bufferSize; i++) {
          const envelope = Math.sin((i / scratchLength) * Math.PI);
          output[scratchStart + i] += Math.sin(i * scratchFreq) * 0.015 * envelope;
        }
      }

      const noiseSource = audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;

      // Filter the noise for that warm vinyl sound
      const noiseFilter = audioContext.createBiquadFilter();
      noiseFilter.type = 'bandpass';
      noiseFilter.frequency.setValueAtTime(800, now);
      noiseFilter.Q.setValueAtTime(0.5, now);

      // High shelf to reduce harshness
      const highShelf = audioContext.createBiquadFilter();
      highShelf.type = 'highshelf';
      highShelf.frequency.setValueAtTime(3000, now);
      highShelf.gain.setValueAtTime(-6, now);

      const noiseGain = audioContext.createGain();
      noiseGain.gain.setValueAtTime(0, now);
      noiseGain.gain.linearRampToValueAtTime(0.4, now + 0.1);
      noiseGain.gain.setValueAtTime(0.35, now + 0.2);
      noiseGain.gain.linearRampToValueAtTime(0.3, now + totalDuration - 0.3);
      noiseGain.gain.linearRampToValueAtTime(0, now + totalDuration);

      noiseSource.connect(noiseFilter);
      noiseFilter.connect(highShelf);
      highShelf.connect(noiseGain);
      noiseGain.connect(analyser);

      noiseSource.start(now);
      noiseSource.stop(now + totalDuration);
    };

    createVinylNoise();

    setTimeout(() => setIsPlaying(false), totalDuration * 1000);
  }, [getAudioContext]);

  return (
    <AudioContextCtx.Provider value={{ getAnalyser, isPlaying, playVintageJazz }}>
      {children}
    </AudioContextCtx.Provider>
  );
};
